import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Send,
  Bot,
  User,
  Sparkles,
  BarChart3,
  Home,
  Users,
  DollarSign,
  Calendar,
  TrendingUp,
  AlertCircle,
  Copy,
  RotateCcw,
  ArrowUp,
  FileText,
  Check,
  Search,
  X,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useBusinessAI } from '../hooks/useBusinessAI';
import { Toast } from './ui/Toast';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  suggestions?: string[];
  vendorBlocks?: { [key: string]: string }; // Bloques por vendedor para copia
}

export function ChatIA() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'online' | 'offline' | 'reconnecting' | 'error'>('online');
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<string>('');
  const [diagnosticInfo, setDiagnosticInfo] = useState<any>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showQuickReplies, setShowQuickReplies] = useState<boolean>(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({ show: false, message: '', type: 'success' });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionCheckInterval = useRef<NodeJS.Timeout | null>(null);

  const { lots, expenses, sellers, loading, error } = useAppData();
  const { processBusinessQuery, businessStats } = useBusinessAI();

  // Mensaje de bienvenida para consultas operacionales
  const welcomeMessage = useMemo(() => ({
    id: '1',
    role: 'assistant' as const,
    content: `¡Hola! 👋 Soy tu **Asistente** 🏠💼\n\n🔍 **BÚSQUEDA SIMPLE - Solo escribe:**\n\n👤 **Nombre del cliente** o 🏠 **Número del lote**\n\n**Ejemplo:** Juan Ramos o Lote 54`,
    timestamp: new Date(),
    suggestions: [
      'Cobros del mes por vendedor',
      'Informe financiero',
      'Vencidas por vendedor',
      'Lotes pagados'
    ]
  }), [businessStats]);

  // Función de diagnóstico completo del sistema
  const runSystemDiagnostic = useCallback(async () => {
    const diagnostic = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connection: (navigator as any).connection || null,
      onLine: navigator.onLine,
      firebase: {
        status: 'unknown' as string,
        error: null as any,
        latency: null as number | null
      },
      data: {
        lots: lots?.length || 0,
        expenses: expenses?.length || 0,
        sellers: sellers?.length || 0,
        hasAnalytics: businessStats !== null
      },
      network: {
        protocol: window.location.protocol,
        host: window.location.host,
        userAgent: navigator.userAgent.includes('Chrome') ? 'Chrome' :
          navigator.userAgent.includes('Firefox') ? 'Firefox' :
            navigator.userAgent.includes('Safari') ? 'Safari' : 'Other'
      }
    };

    try {
      const startTime = Date.now();
      const { db } = await import('../firebaseConfig');
      const { collection, getDocs, limit, query } = await import('firebase/firestore');

      const testQuery = query(collection(db, 'config'), limit(1));
      await getDocs(testQuery);

      diagnostic.firebase.status = 'connected';
      diagnostic.firebase.latency = Date.now() - startTime;

    } catch (error: any) {
      diagnostic.firebase.status = 'error';
      diagnostic.firebase.error = {
        code: error.code || 'unknown',
        message: error.message || 'Unknown error',
        isNGHTTP2: error.message?.includes('NGHTTP2_INTERNAL_ERROR') || false,
        isNetwork: error.message?.includes('network') || error.code === 'network-request-failed',
        isPermission: error.code === 'permission-denied',
        isUnavailable: error.code === 'unavailable'
      };

      setLastError(error.message || 'Error desconocido');
    }

    setDiagnosticInfo(diagnostic);
    return diagnostic;
  }, [lots, expenses, sellers, businessStats]);

  // Monitoreo de conectividad y estado de datos mejorado
  useEffect(() => {
    // Verificar estado de datos y conectividad
    if (loading) {
      setConnectionStatus('reconnecting');
    } else if (error) {
      setConnectionStatus('error');
      setLastError(error.message || 'Error en carga de datos');
    } else if (businessStats) {
      setConnectionStatus('online');
      setRetryCount(0);
      setLastError('');
    } else {
      setConnectionStatus('offline');
    }
  }, [loading, error, businessStats]);

  // Monitoreo automático de conectividad cada 30 segundos
  useEffect(() => {
    const startMonitoring = () => {
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }

      connectionCheckInterval.current = setInterval(async () => {
        if (connectionStatus === 'online') {
          try {
            const { db } = await import('../firebaseConfig');
            const { collection, getDocs, limit, query } = await import('firebase/firestore');
            const testQuery = query(collection(db, 'config'), limit(1));
            await getDocs(testQuery);
          } catch (error: any) {
            console.warn('🔍 Monitoreo detectó problema de conectividad:', error);
            if (error.message?.includes('NGHTTP2_INTERNAL_ERROR')) {
              setConnectionStatus('error');
              setLastError('Error HTTP/2 detectado en monitoreo automático');
            }
          }
        }
      }, 30000); // Cada 30 segundos
    };

    startMonitoring();

    return () => {
      if (connectionCheckInterval.current) {
        clearInterval(connectionCheckInterval.current);
      }
    };
  }, [connectionStatus]);

  // Función de reconexión automática mejorada
  const attemptReconnection = useCallback(async () => {
    if (retryCount < 5) { // Máximo 5 intentos
      setConnectionStatus('reconnecting');
      setRetryCount(prev => prev + 1);

      try {
        // Intentar reconectar a Firebase primero
        const { db } = await import('../firebaseConfig');
        const { collection, getDocs, limit, query } = await import('firebase/firestore');

        // Test de conectividad con Firebase
        console.log('🔍 Probando conectividad con Firebase...');
        const testQuery = query(collection(db, 'config'), limit(1));
        await getDocs(testQuery);

        console.log('✅ Conexión con Firebase exitosa');
        setConnectionStatus('online');
        setRetryCount(0);

        // Refrescar datos después de reconexión exitosa
        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (error: any) {
        console.error('❌ Error de reconexión Firebase:', error);

        // Diagnóstico específico del error
        let errorMessage = 'Error desconocido';
        if (error.code) {
          switch (error.code) {
            case 'unavailable':
              errorMessage = 'Servicio temporalmente no disponible';
              break;
            case 'permission-denied':
              errorMessage = 'Permisos insuficientes';
              break;
            case 'unauthenticated':
              errorMessage = 'No autenticado';
              break;
            case 'network-request-failed':
              errorMessage = 'Fallo de red - verifica tu conexión';
              break;
            default:
              errorMessage = `Firebase error: ${error.code}`;
          }
        } else if (error.message?.includes('NGHTTP2_INTERNAL_ERROR')) {
          errorMessage = 'Error HTTP/2 - problema de protocolo de red';
        } else if (error.message?.includes('network')) {
          errorMessage = 'Error de red - verifica conectividad';
        }

        console.error(`🔥 Diagnóstico: ${errorMessage}`);

        // Si es el último intento, mostrar error detallado
        if (retryCount >= 4) {
          setConnectionStatus('offline');
          const diagnosticMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'assistant',
            content: `🚨 **Error de Conexión Persistente**\n\n**Diagnóstico técnico:**\n• ${errorMessage}\n• Intentos de reconexión: ${retryCount + 1}/5\n• Tipo de error: ${error.code || 'Desconocido'}\n\n**Soluciones recomendadas:**\n• Verifica tu conexión a internet\n• Desactiva temporalmente VPN si usas una\n• Recarga la página completamente (Ctrl/Cmd + F5)\n• Contacta al administrador si persiste\n\n**Detalles técnicos para soporte:**\n\`\`\`\n${error.message || 'Sin detalles'}\n\`\`\``,
            timestamp: new Date(),
            suggestions: [
              'Recargar página completa',
              'Verificar configuración de red',
              'Contactar soporte técnico',
              'Intentar desde otro navegador'
            ]
          };
          setMessages(prev => [...prev, diagnosticMessage]);
        } else {
          // Reintento automático con delay exponencial
          setTimeout(() => {
            attemptReconnection();
          }, 2000 * Math.pow(2, retryCount)); // 2s, 4s, 8s, 16s
        }
      }
    }
  }, [retryCount, setMessages]);

  // Inicializar mensajes vacíos
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([]);
    }
  }, [messages.length]);

  // Auto scroll al final
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);





  // Función para obtener contexto completo del negocio
  const getBusinessContext = useCallback(() => {
    if (!businessStats) return "Sin datos disponibles";

    return `Contexto del negocio Bimobi:
- Total de lotes: ${businessStats.totalLots}
- Lotes disponibles: ${businessStats.availableLots}
- Lotes vendidos: ${businessStats.soldLots}
- Lotes a crédito: ${businessStats.creditLots}
- Total clientes: ${businessStats.totalClients}
- Ingresos hoy: Q${businessStats.todayRevenue.toLocaleString()}
- Fecha actual: ${new Date().toLocaleDateString('es-GT')}`;
  }, [businessStats]);

  // Procesamiento con IA operacional de negocio
  const processWithAI = async (query: string): Promise<{ content: string; vendorBlocks?: { [key: string]: string } }> => {
    try {
      // Verificar estado de conectividad antes de procesar
      if (connectionStatus === 'offline') {
        return {
          content: '🔌 **Sin conexión a datos**\n\nNo puedo acceder a los datos actualizados para consultas específicas de clientes.\n\n💡 **Cuando tengas conexión podré ayudarte con:**\n• Buscar información de clientes específicos\n• Estado de pagos y cuotas\n• Lotes disponibles actualizados\n• Datos financieros en tiempo real\n\n¿Intentamos reconectar?'
        };
      }

      // Verificar que tenemos datos
      if (!businessStats) {
        return {
          content: '⚠️ **Datos no disponibles**\n\nActualmente no tengo acceso a los datos del negocio.\n\n🔄 **Cargando información...**\nPor favor espera unos momentos o recarga la página.'
        };
      }

      // Usar la IA operacional de negocio
      const aiResult = processBusinessQuery(query);

      // Agregar indicador de confianza y fuente de datos
      const confidenceIndicator = aiResult.confidence ?
        (aiResult.confidence > 0.8 ? '🎯 **Datos encontrados**' :
          aiResult.confidence > 0.5 ? '💡 **Resultado parcial**' :
            '🤔 **Búsqueda amplia**') : '';

      const dataSourceInfo = aiResult.dataFound ?
        `\n\n📊 **Datos en tiempo real** desde base de datos Firebase • **${new Date().toLocaleString('es-GT')}**` :
        `\n\n🔍 **Búsqueda realizada** en toda la base de datos • **${new Date().toLocaleString('es-GT')}**`;

      const content = `${aiResult.answer}${dataSourceInfo}${confidenceIndicator ? `\n\n${confidenceIndicator}` : ''}`;

      return {
        content,
        vendorBlocks: aiResult.vendorBlocks || undefined
      };

    } catch (error: any) {
      console.error('Error en processWithAI:', error);

      // Respuesta de fallback específica para consultas de negocio
      return {
        content: `❌ **Error procesando consulta**\n\nHubo un problema técnico al buscar la información.\n\n💡 **Intentos alternativos:**\n• Verifica el nombre exacto del cliente\n• Prueba con "¿Cuántos lotes están disponibles?"\n• Recarga la página si persiste el problema\n\n🔄 **El sistema sigue funcionando** para consultas básicas.`
      };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Verificar conectividad antes de procesar
    if (connectionStatus === 'offline') {
      const offlineMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: '🔌 **Sin conexión a datos**\n\nNo puedo procesar tu consulta en este momento debido a problemas de conectividad.\n\n**Opciones disponibles:**\n• Intenta recargar la página\n• Verifica tu conexión a internet\n• Espera unos momentos y vuelve a intentar\n\n¿Te gustaría que intente reconectar automáticamente?',
        timestamp: new Date(),
        suggestions: [
          'Intentar reconectar',
          'Recargar página',
          'Ver estado del sistema',
          'Contactar soporte'
        ]
      };
      setMessages(prev => [...prev, offlineMessage]);
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    try {
      // Verificar comandos especiales
      if (currentInput.toLowerCase().includes('reconectar') || currentInput.toLowerCase().includes('recargar')) {
        attemptReconnection();
        const reconnectMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: '🔄 **Intentando reconectar...**\n\nEstoy tratando de restablecer la conexión con los datos. Esto puede tomar unos momentos.\n\n⏱️ La página se recargará automáticamente para aplicar la reconexión.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, reconnectMessage]);
        setIsLoading(false);
        return;
      }

      // Comando de diagnóstico del sistema (simplificado)
      if (currentInput.toLowerCase().includes('diagnóstico') || currentInput.toLowerCase().includes('diagnostico') || currentInput.toLowerCase().includes('debug')) {
        const diagnosticMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `🔍 **Diagnóstico Rápido del Sistema**\n\n**📊 Estado de Datos:**\n• Lotes: ${lots?.length || 0} registros\n• Movimientos: ${expenses?.length || 0} registros\n• Vendedores: ${sellers?.length || 0} registros\n• Business Stats: ${businessStats ? '✅ Disponible' : '❌ No disponible'}\n• Estado de carga: ${loading ? '🔄 Cargando' : '✅ Completo'}\n• Error actual: ${error ? `❌ ${error}` : '✅ Sin errores'}\n\n**🌐 Estado de Conexión:**\n• Estado: ${connectionStatus}\n• Último error: ${lastError || 'Ninguno'}\n• Navegador en línea: ${navigator.onLine ? '✅ Sí' : '❌ No'}\n• Protocolo: ${window.location.protocol}\n• Host: ${window.location.host}\n\n**🚨 SOLUCIONES PARA NGHTTP2_INTERNAL_ERROR:**\n• Desactivar VPN temporalmente\n• Usar modo incógnito del navegador\n• Cambiar a otra red WiFi\n• Usar hotspot del móvil\n• Limpiar caché del navegador\n\n**🕐 Timestamp:** ${new Date().toLocaleString('es-GT')}`,
          timestamp: new Date(),
          suggestions: [
            'Intentar reconectar',
            'Recargar página completa',
            'Cambiar de red',
            'Usar modo incógnito'
          ]
        };
        setMessages(prev => [...prev, diagnosticMessage]);
        setIsLoading(false);
        return;
      }

      // Procesar consulta con IA mejorada para acceso remoto
      let aiResponse;
      try {
        aiResponse = await processWithAI(currentInput);
      } catch (queryError) {
        console.warn('Error en processWithAI, usando fallback:', queryError);
        aiResponse = {
          content: '⚠️ **Datos temporalmente inaccesibles**\n\nHay un problema temporal con el acceso a los datos. La información mostrada puede no estar actualizada.\n\n**Para acceso remoto:**\n• Asegúrate de tener conexión estable a internet\n• Los datos se sincronizan automáticamente con Firebase\n• Recarga la página si persisten los problemas\n\n🌐 **Acceso disponible desde cualquier lugar** en bimobi.app'
        };
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: aiResponse.content,
        timestamp: new Date(),
        vendorBlocks: aiResponse.vendorBlocks, // Incluir bloques de vendedores si los hay
        suggestions: [
          '¿Algo más que necesites?',
          'Ver estadísticas detalladas',
          'Generar reporte',
          'Consultar otro tema'
        ]
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error crítico al procesar consulta:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '❌ **Error del sistema**\n\nHubo un problema técnico al procesar tu consulta.\n\n**Detalles técnicos:**\n• Es posible que haya problemas de conectividad\n• Los datos pueden estar temporalmente inaccesibles\n• Error de tipo: ' + (error instanceof Error ? error.message : 'Desconocido') + '\n\n**Soluciones:**\n• Recarga la página completamente\n• Verifica tu conexión a internet\n• Si el problema persiste, contacta al administrador',
        timestamp: new Date(),
        suggestions: [
          'Recargar página',
          'Intentar reconectar',
          'Ver estado del sistema',
          'Contactar soporte'
        ]
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
    inputRef.current?.focus();
  };

  const clearChat = () => {
    setMessages([]); // Reiniciar con chat vacío
  };

  // Función mejorada para copiar con feedback visual
  const copyMessage = async (content: string, messageId: string, format: 'plain' | 'formatted' | 'whatsapp' = 'plain') => {
    try {
      let textToCopy = content;

      if (format === 'plain') {
        // Remover markdown y HTML
        textToCopy = content
          .replace(/\*\*(.*?)\*\*/g, '$1') // **bold** -> bold
          .replace(/\*(.*?)\*/g, '$1')     // *italic* -> italic
          .replace(/<br\s*\/?>/g, '\n')    // <br> -> \n
          .replace(/<[^>]*>/g, '')         // remover HTML
          .trim();
      } else if (format === 'whatsapp') {
        // Convertir markdown a formato WhatsApp
        textToCopy = content
          .replace(/\*\*(.*?)\*\*/g, '*$1*')  // **bold** -> *bold*
          .replace(/\*(.*?)\*/g, '_$1_')      // *italic* -> _italic_
          .replace(/<br\s*\/?>/g, '\n')
          .replace(/<[^>]*>/g, '')
          .trim();
      }
      // format 'formatted' mantiene el contenido original

      await navigator.clipboard.writeText(textToCopy);
      setCopiedMessageId(messageId);
      setToast({ show: true, message: 'Copiado con formato', type: 'success' });

      // Feedback visual por 2 segundos
      setTimeout(() => setCopiedMessageId(null), 2000);

    } catch (error) {
      console.error('Error al copiar:', error);
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = content.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1');
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedMessageId(messageId);
      setToast({ show: true, message: 'Copiado al portapapeles', type: 'success' });
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
  };



  // Función para copiar todo el chat
  const copyAllChat = () => {
    const allMessages = messages
      .map(msg => {
        const role = msg.role === 'user' ? '👤 Usuario' : '🤖 Asistente';
        const time = msg.timestamp.toLocaleString('es-GT');
        const content = msg.content
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/<br\s*\/?>/g, '\n')
          .replace(/<[^>]*>/g, '')
          .trim();
        return `${role} (${time}):\n${content}`;
      })
      .join('\n\n---\n\n');

    const fullChat = `🏠 BIMOBI - HISTORIAL DE CHAT\n\nGenerado: ${new Date().toLocaleString('es-GT')}\n\n${allMessages}`;

    navigator.clipboard.writeText(fullChat);
    setCopiedMessageId('all-chat');
    setToast({ show: true, message: 'Chat completo copiado', type: 'success' });
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  // Respuestas rápidas predefinidas
  const quickReplies = [
    'Cobros del mes por vendedor',
    'Informe financiero',
    'Vencidas por vendedor',
    'Lotes pagados',
    '¿Cuántos lotes están disponibles?',
    'Resumen de ventas del mes',
    'Lista de vendedores activos',
    'Estado financiero general'
  ];

  // Filtrar mensajes por término de búsqueda
  const filteredMessages = useMemo(() => {
    if (!searchTerm.trim()) return messages;

    return messages.filter(message =>
      message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [messages, searchTerm]);

  // Resaltar texto en mensajes
  const highlightText = (text: string, term: string) => {
    if (!term.trim()) return formatMessage(text);

    const regex = new RegExp(`(${term})`, 'gi');
    const highlightedText = text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    return formatMessage(highlightedText);
  };

  const formatMessage = (content: string) => {
    // Convertir markdown básico a HTML
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] max-w-6xl mx-auto bg-card rounded-2xl shadow-none border border-border overflow-hidden">
      {/* Header mejorado con más opciones */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Asistente</h1>
          {messages.length > 0 && (
            <span className="ml-2 text-sm text-gray-500">({messages.length} mensajes)</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
                title="Buscar en chat"
              >
                <Search className="h-5 w-5 text-muted-foreground" />
              </button>
              <button
                onClick={copyAllChat}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
                title="Copiar todo el chat"
              >
                {copiedMessageId === 'all-chat' ? (
                  <Check className="h-5 w-5 text-emerald-500" />
                ) : (
                  <FileText className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            </>
          )}
          <button
            onClick={() => setShowQuickReplies(!showQuickReplies)}
            className="p-2 hover:bg-white/5 rounded-full transition-colors"
            title="Respuestas rápidas"
          >
            <Zap className="h-5 w-5 text-muted-foreground" />
          </button>
          <button
            onClick={clearChat}
            className="p-2 hover:bg-white/5 rounded-full transition-colors"
            title="Nuevo chat"
          >
            <RotateCcw className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      {showSearch && (
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar en conversación..."
                className="w-full px-3 py-2 bg-secondary border border-input rounded-lg focus:outline-none focus:ring-1 focus:ring-white/20 text-sm text-foreground placeholder:text-muted-foreground"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowSearch(false)}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {searchTerm && (
            <div className="mt-2 text-xs text-muted-foreground">
              {filteredMessages.length} resultado{filteredMessages.length !== 1 ? 's' : ''} encontrado{filteredMessages.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Panel de respuestas rápidas */}
      {showQuickReplies && (
        <div className="p-4 border-b border-border bg-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-foreground">Respuestas Rápidas</h3>
            <button
              onClick={() => setShowQuickReplies(false)}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-white/5 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {quickReplies.map((reply, index) => (
              <button
                key={index}
                onClick={() => {
                  setInputMessage(reply);
                  setShowQuickReplies(false);
                  inputRef.current?.focus();
                }}
                className="text-left p-2 text-sm text-foreground bg-secondary border border-border rounded-lg hover:bg-accent hover:border-input transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Messages Area modo oscuro */}
      <ScrollArea className="flex-1 p-6 bg-card overflow-y-auto max-h-[calc(100vh-20rem)]">
        <div className="space-y-6 min-h-full">


          {filteredMessages.map((message, index) => (
            <div key={message.id} className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {message.role === 'assistant' && (
                <div className="flex-shrink-0">
                  <div className="p-2 bg-secondary rounded-xl border border-border">
                    <Bot className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              )}

              <div className={`max-w-[85%] ${message.role === 'user' ? 'order-first' : ''}`}>
                <div
                  className={`p-4 rounded-2xl ${message.role === 'user'
                    ? 'bg-secondary text-foreground ml-auto border border-border'
                    : 'bg-transparent text-foreground border border-transparent'
                    }`}
                >
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: showSearch && searchTerm ? highlightText(message.content, searchTerm) : formatMessage(message.content) }}
                  />

                  {message.role === 'assistant' && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyMessage(message.content, message.id, 'whatsapp')}
                            className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors ${copiedMessageId === message.id
                              ? 'text-green-600 bg-green-50'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                              }`}
                            title="Copiar con formato"
                          >
                            {copiedMessageId === message.id ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                            {copiedMessageId === message.id ? 'Copiado' : 'Copiar'}
                          </button>
                        </div>
                      </div>

                      <span className="text-xs text-muted-foreground">
                        {message.timestamp.toLocaleTimeString('es-GT', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}

                  {/* Botones de copia por vendedor si hay bloques disponibles */}
                  {message.role === 'assistant' && message.vendorBlocks && Object.keys(message.vendorBlocks).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-gray-600">📋 Copiar por vendedor:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(message.vendorBlocks).map(([vendedor, bloque]) => (
                          <button
                            key={vendedor}
                            onClick={() => {
                              copyMessage(bloque, `${message.id}-${vendedor}`, 'whatsapp');
                            }}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${copiedMessageId === `${message.id}-${vendedor}`
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                              }`}
                          >
                            {copiedMessageId === `${message.id}-${vendedor}` ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-3 w-3" />
                                Copiado
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <Copy className="h-3 w-3" />
                                {vendedor}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        💡 Cada botón copia el reporte completo de ese vendedor con formato
                      </div>
                    </div>
                  )}
                </div>


              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0">
                  <div className="p-2 bg-secondary rounded-xl border border-border">
                    <User className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator modo oscuro */}
          {isLoading && (
            <div className="flex gap-4 justify-start">
              <div className="flex-shrink-0">
                <div className="p-2 bg-secondary rounded-xl border border-border">
                  <Bot className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
              <div className="bg-transparent text-foreground border border-transparent p-4 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-500/70 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500/70 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-pink-500/70 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-muted-foreground">Analizando datos...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </ScrollArea>

      {/* Acciones rápidas arriba del input estilo Grok */}
      {!inputMessage.trim() && (
        <div className="px-4 pb-2">
          <div className="flex gap-3 overflow-x-auto justify-center">
            {[
              { icon: DollarSign, text: 'Cobros mes', query: 'Cobros del mes por vendedor' },
              { icon: TrendingUp, text: 'Informe financiero', query: 'Informe financiero' },
              { icon: AlertCircle, text: 'Vencidas', query: 'Vencidas por vendedor' },
              { icon: CheckCircle2, text: 'Lotes pagados', query: 'Lotes pagados' }
            ].map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestionClick(action.query)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-accent rounded-xl border border-border hover:border-input whitespace-nowrap transition-all duration-200 text-muted-foreground hover:text-foreground"
              >
                <action.icon className="h-4 w-4" />
                {action.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area estilo Linear - modo oscuro */}
      <div className="p-4 bg-card">
        <div className="flex items-center gap-3 bg-secondary rounded-2xl p-4 border border-border focus-within:border-input transition-colors">
          {/* Input Field */}
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ingresa el número del lote o nombre comprador"
              className="w-full bg-transparent text-foreground placeholder:text-muted-foreground/50 focus:outline-none text-base py-2"
              disabled={isLoading}
            />
          </div>

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed border border-border"
          >
            <ArrowUp className="w-5 h-5 text-foreground" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground/40 mt-3 text-center">
          🤖 Asistente Operacional • Consultas de negocio en tiempo real • Sin costo adicional • **${new Date().toLocaleString('es-GT')}**
        </p>
      </div>

      {/* Toast de notificaciones */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
}
